import {
  VISITOR_ID_PATTERN,
  VISITOR_ID_ALGO_VERSION,
  VISITOR_STORAGE_KEY,
} from './constants.js';

export type VisitorSource = 'uuid-v7' | 'fpjs' | 'fallback';

export type StoredVisitor = {
  visitorId: string;
  source: VisitorSource;
  /** Library / algorithm generation used when the id was created */
  algoVersion: string;
};

function isStoredVisitor(value: unknown): value is StoredVisitor {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.visitorId === 'string' &&
    VISITOR_ID_PATTERN.test(record.visitorId) &&
    (record.source === 'uuid-v7' ||
      record.source === 'fpjs' ||
      record.source === 'fallback') &&
    typeof record.algoVersion === 'string'
  );
}

export function readVisitorFromStorage(): StoredVisitor | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(VISITOR_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isStoredVisitor(parsed)) {
      window.localStorage.removeItem(VISITOR_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function writeVisitorToStorage(visitor: StoredVisitor): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify(visitor));
  } catch {
    // Private mode / quota — caller still gets in-memory id for this session
  }
}

export function createStoredVisitor(
  visitorId: string,
  source: VisitorSource,
  algoVersion: string = VISITOR_ID_ALGO_VERSION,
): StoredVisitor {
  return {
    visitorId,
    source,
    algoVersion,
  };
}
